// src/services/pairingService.js - Science-Based Wine-Meal Pairing Integration

/**
 * Wine-Meal Pairing Service
 *
 * Implements a science-based algorithm for food and wine pairing compatibility.
 * Based on molecular interaction, taste chemistry, and textural dynamics.
 *
 * Algorithm Features:
 * - 7-component food analysis (fat, acidity, sweetness, bitterness, umami, spice, intensity)
 * - 5-component wine analysis (acidity, sweetness, tannin, alcohol, intensity)
 * - 13 interaction rules with scientific rationale
 * - Quantified scoring with detailed explanations
 */

export class PairingService {
  constructor() {
    this.initialized = true;
  }

  /**
   * Enhanced Core 1-to-1 Pairing Algorithm (Version 4)
   * Implements the advanced science-based scoring engine with personalization
   * @param {Object} foodProfile - Food characteristics (enhanced with texture, temp)
   * @param {Object} wineProfile - Wine characteristics (enhanced with quality, serving temp)
   * @param {Object} userProfile - User preferences (tannin sensitivity, acid preference)
   * @param {Object} context - Contextual factors (season, occasion)
   * @returns {Object} Pairing analysis with score, interpretation, and rationale
   */
  getAdvancedPairingScore(
    foodProfile,
    wineProfile,
    userProfile = {},
    context = {}
  ) {
    if (!foodProfile || !wineProfile) {
      return {
        score: 0,
        interpretation: "N/A",
        rationale: [],
        colorClass: "text-gray-400",
      };
    }

    const userPrefs = this._normalizeUserProfile(userProfile);
    const qualityModifier = this._calculateQualityModifier(wineProfile);

    let score = 0;
    const rationale = [];

    // Apply scoring rules
    score += this._applyPositiveInteractions(
      foodProfile,
      wineProfile,
      userPrefs,
      rationale
    );
    score += this._applyNegativeInteractions(
      foodProfile,
      wineProfile,
      userPrefs,
      qualityModifier,
      rationale
    );
    score += this._applyVetoRules(
      foodProfile,
      wineProfile,
      userPrefs,
      qualityModifier,
      rationale
    );
    score += this._applyTemperatureAnalysis(wineProfile, rationale);

    const interpretation = this._getScoreInterpretation(score);

    // Default rationale if no interactions detected
    if (rationale.length === 0) {
      rationale.push({
        points: "0",
        reason: "No strong interactions detected. This is a neutral pairing.",
      });
    }

    return {
      score,
      interpretation: interpretation.text,
      rationale,
      colorClass: interpretation.colorClass,
    };
  }

  /**
   * Enhanced Core 1-to-1 Pairing Algorithm (Version 5)
   * Implements advanced molecular-level pairing with aromatic compounds and problem ingredients
   * @param {Object} foodProfile - Food characteristics (v5: includes aromatic compounds, problem ingredients)
   * @param {Object} wineProfile - Wine characteristics (v5: includes aromatic compounds)
   * @param {Object} userProfile - User preferences (tannin sensitivity, acid preference)
   * @param {Object} context - Contextual factors (season, occasion)
   * @returns {Object} Pairing analysis with score, interpretation, and rationale
   */
  getAdvancedPairingScore_v5(
    foodProfile,
    wineProfile,
    userProfile = {},
    context = {}
  ) {
    if (!foodProfile || !wineProfile) {
      return {
        score: 0,
        interpretation: "N/A",
        rationale: [],
        colorClass: "text-gray-400",
      };
    }

    let score = 0;
    const rationale = [];

    // --- Base Component Matching (v4 rules) ---
    score += this._applyBaseMatching(foodProfile, wineProfile, rationale);

    // --- 1. Enhanced Tannin Quality Index (TQI) Logic ---
    score += this._applyTanninQualityLogic(foodProfile, wineProfile, rationale);

    // --- 2. Dynamic Temperature Modeling ---
    score += this._applyTemperatureModeling(
      foodProfile,
      wineProfile,
      rationale
    );

    // --- 3. Aromatic Bridge Logic (NEW in v5) ---
    score += this._applyAromaticBridgeLogic(
      foodProfile,
      wineProfile,
      rationale
    );

    // --- 4. Problem Ingredient Solver (NEW in v5) ---
    score += this._applyProblemIngredientSolver(
      foodProfile,
      wineProfile,
      rationale
    );

    const interpretation = this._getScoreInterpretation(score);

    // Default rationale if no interactions detected
    if (rationale.length === 0) {
      rationale.push({
        points: "0",
        reason: "No strong interactions detected. This is a neutral pairing.",
      });
    }

    return {
      score,
      interpretation: interpretation.text,
      rationale,
      colorClass: interpretation.colorClass,
    };
  }

  /**
   * Normalize user profile with defaults
   * @private
   */
  _normalizeUserProfile(userProfile) {
    return {
      tanninSensitivity: userProfile.tanninSensitivity || 1.0,
      acidPreference: userProfile.acidPreference || 1.0,
    };
  }

  /**
   * Calculate wine quality modifier
   * @private
   */
  _calculateQualityModifier(wineProfile) {
    return 1 - (1 - (wineProfile.tanninQuality || 0.8)) * 0.5;
  }

  /**
   * Apply positive interaction rules
   * @private
   */
  _applyPositiveInteractions(foodProfile, wineProfile, userPrefs, rationale) {
    let score = 0;

    // Synergistic Pairing: Protein/fat binds with tannins
    if (foodProfile.fat > 7 && wineProfile.tannin > 7) {
      score += 10;
      rationale.push({
        points: "+10",
        reason: "Synergistic Pairing: Rich food softens high tannins.",
      });
    }

    // Palate Cleansing: Acidity cuts through fat (personalized)
    if (foodProfile.fat > 5 && wineProfile.acidity > 6) {
      const adjustedScore = 7 * userPrefs.acidPreference;
      score += adjustedScore;
      rationale.push({
        points: `+${adjustedScore.toFixed(0)}`,
        reason:
          userPrefs.acidPreference !== 1.0
            ? "Palate Cleansing: Acidity cuts richness (Adjusted by your acid preference)."
            : "Palate Cleansing: Acidity cuts through richness.",
      });
    }

    // Spice Soothing: Sweetness cools spice heat
    if (foodProfile.spice > 4 && wineProfile.sweetness > 3) {
      score += 7;
      rationale.push({
        points: "+7",
        reason: "Spice Soothing: Sweetness cools spice heat.",
      });
    }

    // Intensity Match: Balanced power levels
    if (Math.abs(foodProfile.intensity - wineProfile.intensity) <= 2) {
      score += 5;
      rationale.push({
        points: "+5",
        reason: "Intensity Match: Food and wine are well-balanced in power.",
      });
    }

    // Acid Balance: Wine maintains freshness
    if (wineProfile.acidity >= foodProfile.acidity) {
      score += 3;
      rationale.push({
        points: "+3",
        reason: "Acid Balance: Wine's acidity holds up to the food's.",
      });
    }

    // Sweet Balance: Wine not dried out by food
    if (wineProfile.sweetness >= foodProfile.sweetness) {
      score += 3;
      rationale.push({
        points: "+3",
        reason: "Sweet Balance: Wine is not dried out by food's sweetness.",
      });
    }

    return score;
  }

  /**
   * Apply negative interaction rules
   * @private
   */
  _applyNegativeInteractions(
    foodProfile,
    wineProfile,
    userPrefs,
    qualityModifier,
    rationale
  ) {
    let score = 0;

    // Umami Clash: Savory food makes tannic wine bitter (personalized)
    if (foodProfile.umami > 6 && wineProfile.tannin > 5) {
      const adjustedPenalty =
        -10 * userPrefs.tanninSensitivity * qualityModifier;
      score += adjustedPenalty;
      rationale.push({
        points: adjustedPenalty.toFixed(0),
        reason:
          "Umami Clash: Penalty adjusted by tannin quality and your sensitivity.",
      });
    }

    // Spice Clash: Alcohol amplifies heat
    if (foodProfile.spice > 5 && wineProfile.alcohol > 6) {
      score -= 10;
      rationale.push({
        points: "-10",
        reason: "Spice Clash: Alcohol amplifies spice heat.",
      });
    }

    // Bitter Collision: Additive bitterness (personalized)
    if (foodProfile.bitterness > 5 && wineProfile.tannin > 6) {
      const adjustedPenalty =
        -7 * userPrefs.tanninSensitivity * qualityModifier;
      score += adjustedPenalty;
      rationale.push({
        points: adjustedPenalty.toFixed(0),
        reason:
          "Bitter Collision: Penalty adjusted by tannin quality and your sensitivity.",
      });
    }

    // Intensity Mismatch: One dominates the other
    if (Math.abs(foodProfile.intensity - wineProfile.intensity) > 4) {
      score -= 5;
      rationale.push({
        points: "-5",
        reason: "Intensity Mismatch: One element will dominate the other.",
      });
    }

    return score;
  }

  /**
   * Apply veto rules
   * @private
   */
  _applyVetoRules(
    foodProfile,
    wineProfile,
    userPrefs,
    qualityModifier,
    rationale
  ) {
    let score = 0;

    // VETO: Acid Flattens - Wine tastes dull
    if (wineProfile.acidity < foodProfile.acidity) {
      score -= 15;
      rationale.push({
        points: "-15",
        reason:
          "VETO - Acid Flattens: Wine tastes flat next to more acidic food.",
      });
    }

    // VETO: Sweet Dries - Wine tastes harsh
    if (wineProfile.sweetness < foodProfile.sweetness) {
      score -= 15;
      rationale.push({
        points: "-15",
        reason: "VETO - Sweet Dries: Wine tastes bitter next to sweeter food.",
      });
    }

    // VETO: Fishy Metal - Chemical reaction (personalized)
    if (foodProfile.isOilyFish && wineProfile.tannin > 4) {
      const adjustedPenalty =
        -20 * userPrefs.tanninSensitivity * qualityModifier;
      score += adjustedPenalty;
      rationale.push({
        points: adjustedPenalty.toFixed(0),
        reason:
          "VETO - Fishy Metal: Penalty adjusted by tannin quality and your sensitivity.",
      });
    }

    return score;
  }

  /**
   * Apply temperature analysis
   * @private
   */
  _applyTemperatureAnalysis(wineProfile, rationale) {
    if (!wineProfile.idealTemp || !wineProfile.currentTemp) {
      return 0;
    }

    const tempDiff = Math.abs(wineProfile.currentTemp - wineProfile.idealTemp);
    if (tempDiff <= 1) {
      rationale.push({
        points: "+3",
        reason: "Temperature Bonus: Wine is served at its ideal temperature.",
      });
      return 3;
    } else if (tempDiff > 3) {
      const tempPenalty = -tempDiff * 1.5;
      rationale.push({
        points: tempPenalty.toFixed(0),
        reason: `Temperature Penalty: Wine is served ${tempDiff}°C from ideal.`,
      });
      return tempPenalty;
    }
    return 0;
  }

  /**
   * Get score interpretation
   * @private
   */
  _getScoreInterpretation(score) {
    if (score > 15) {
      return { text: "Excellent Pairing", colorClass: "text-green-400" };
    } else if (score >= 5) {
      return { text: "Good Pairing", colorClass: "text-blue-400" };
    } else if (score > -5) {
      return { text: "Neutral / Risky", colorClass: "text-yellow-400" };
    } else {
      return { text: "Problematic Pairing", colorClass: "text-red-400" };
    }
  }

  /**
   * Get confidence level based on score
   * @private
   */
  _getConfidenceLevel(score) {
    if (score > 10) return "high";
    if (score > 0) return "medium";
    return "low";
  }

  /**
   * Legacy method for backward compatibility
   */
  getPairingScore(foodProfile, wineProfile) {
    return this.getAdvancedPairingScore(foodProfile, wineProfile);
  }

  /**
   * Calculate similarity between two wine profiles (NEW in V4)
   * @param {Object} profileA - First wine profile
   * @param {Object} profileB - Second wine profile
   * @returns {number} Similarity score (0-1, higher is better)
   */
  calculateSimilarity(profileA, profileB) {
    if (!profileA || !profileB) return 0;

    const keys = ["acidity", "sweetness", "tannin", "alcohol", "intensity"];
    const squaredDifferences = keys.map((key) =>
      Math.pow((profileA[key] || 0) - (profileB[key] || 0), 2)
    );
    const sumOfSquares = squaredDifferences.reduce((sum, val) => sum + val, 0);
    const distance = Math.sqrt(sumOfSquares);

    // Convert distance to similarity (0 to 1 scale, higher is better)
    const maxPossibleDistance = Math.sqrt(keys.length * Math.pow(10, 2));
    return 1 - distance / maxPossibleDistance;
  }

  /**
   * Extract/normalize enhanced food profile from menu item (V4)
   * Converts menu item data to standardized enhanced food profile
   * @param {Object} menuItem - Restaurant menu item
   * @returns {Object} Enhanced food profile for algorithm
   */
  extractFoodProfile(menuItem) {
    // If already has profile, use it
    if (menuItem.profile) {
      return menuItem.profile;
    }

    // Extract from AI enhanced data or item properties
    const profile = {
      fat: this.extractNumericValue(
        menuItem,
        ["fat", "richness", "oiliness"],
        1,
        10,
        5
      ),
      acidity: this.extractNumericValue(
        menuItem,
        ["acidity", "sourness", "tartness"],
        1,
        10,
        1
      ),
      sweetness: this.extractNumericValue(
        menuItem,
        ["sweetness", "sugar"],
        1,
        10,
        1
      ),
      bitterness: this.extractNumericValue(
        menuItem,
        ["bitterness", "bitter"],
        1,
        10,
        2
      ),
      umami: this.extractNumericValue(
        menuItem,
        ["umami", "savory", "meaty"],
        1,
        10,
        4
      ),
      spice: this.extractNumericValue(
        menuItem,
        ["spice", "heat", "pungency"],
        0,
        10,
        0
      ),
      intensity: this.extractNumericValue(
        menuItem,
        ["intensity", "boldness", "power"],
        1,
        10,
        5
      ),
      isOilyFish: this.detectOilyFish(menuItem),

      // NEW V4 Fields
      texture: this.extractTextValue(
        menuItem,
        ["texture", "mouthfeel"],
        "tender"
      ),
      temp: this.extractTextValue(
        menuItem,
        ["temperature", "temp", "serving"],
        "hot"
      ),
    };

    return profile;
  }

  /**
   * Extract/normalize enhanced wine profile from wine item (V4)
   * Converts wine data to standardized enhanced wine profile
   * @param {Object} wine - Wine item
   * @returns {Object} Enhanced wine profile for algorithm
   */
  extractWineProfile(wine) {
    // If already has profile, use it
    if (wine.profile) {
      return wine.profile;
    }

    // Extract from AI enhanced data or wine properties
    const profile = {
      acidity: this.extractNumericValue(
        wine,
        ["acidity", "acid", "tartness"],
        1,
        10,
        6
      ),
      sweetness: this.extractNumericValue(
        wine,
        ["sweetness", "residualSugar", "rs"],
        1,
        10,
        1
      ),
      tannin: this.extractNumericValue(
        wine,
        ["tannin", "tannins", "astringency"],
        0,
        10,
        3
      ),
      alcohol: this.extractAlcoholLevel(wine),
      intensity: this.extractNumericValue(
        wine,
        ["intensity", "body", "weight"],
        1,
        10,
        5
      ),

      // NEW V4 Fields
      tanninQuality: this.extractNumericValue(
        wine,
        ["tanninQuality", "quality"],
        0.1,
        1.0,
        0.8
      ),
      mouthfeel: this.extractTextValue(
        wine,
        ["mouthfeel", "texture"],
        "smooth"
      ),
      tags: this.extractArrayValue(wine, ["tags", "characteristics"], []),
      idealTemp: this.extractNumericValue(
        wine,
        ["idealTemp", "servingTemp"],
        0,
        25,
        16
      ),
      currentTemp: this.extractNumericValue(
        wine,
        ["currentTemp", "temperature"],
        0,
        25,
        16
      ),
    };

    return profile;
  }

  /**
   * Helper method to extract text values from item properties
   */
  extractTextValue(item, possibleKeys, defaultValue) {
    for (const key of possibleKeys) {
      if (item[key] !== undefined && item[key] !== null) {
        return String(item[key]);
      }

      // Check in aiEnhanced data
      if (item.aiEnhanced && item.aiEnhanced[key] !== undefined) {
        return String(item.aiEnhanced[key]);
      }
    }
    return defaultValue;
  }

  /**
   * Helper method to extract array values from item properties
   */
  extractArrayValue(item, possibleKeys, defaultValue) {
    for (const key of possibleKeys) {
      if (Array.isArray(item[key])) {
        return item[key];
      }

      // Check in aiEnhanced data
      if (item.aiEnhanced && Array.isArray(item.aiEnhanced[key])) {
        return item.aiEnhanced[key];
      }
    }
    return defaultValue;
  }

  /**
   * Helper method to extract numeric values from item properties
   */
  extractNumericValue(item, possibleKeys, min, max, defaultValue) {
    for (const key of possibleKeys) {
      if (item[key] !== undefined && item[key] !== null) {
        const value = parseFloat(item[key]);
        if (!isNaN(value)) {
          return Math.max(min, Math.min(max, value));
        }
      }

      // Check in aiEnhanced data
      if (item.aiEnhanced && item.aiEnhanced[key] !== undefined) {
        const value = parseFloat(item.aiEnhanced[key]);
        if (!isNaN(value)) {
          return Math.max(min, Math.min(max, value));
        }
      }
    }
    return defaultValue;
  }

  /**
   * Extract alcohol level and convert to 1-10 scale
   */
  extractAlcoholLevel(wine) {
    const alcohol = this.extractNumericValue(
      wine,
      ["alcohol", "abv", "alcoholContent"],
      0,
      20,
      13
    );

    // Convert percentage to 1-10 scale
    if (alcohol < 12.5) return Math.round(alcohol / 4.17); // 1-3
    if (alcohol <= 14) return Math.round(3 + (alcohol - 12.5) * 2); // 4-6
    return Math.round(6 + Math.min(4, (alcohol - 14) * 1.33)); // 7-10
  }

  /**
   * Detect if menu item contains oily fish
   */
  detectOilyFish(menuItem) {
    const text = (
      menuItem.name +
      " " +
      (menuItem.description || "")
    ).toLowerCase();
    const oilyFishTerms = [
      "salmon",
      "mackerel",
      "sardine",
      "tuna",
      "anchovy",
      "herring",
      "trout",
    ];
    return oilyFishTerms.some((term) => text.includes(term));
  }

  /**
   * Find optimal wine pairings for a given meal (Enhanced V4)
   * @param {Object|Array} meal - Single meal or array of meals
   * @param {Array} wineInventory - Available wines to choose from
   * @param {Object} userProfile - User preferences (tannin sensitivity, acid preference)
   * @param {Object} context - Contextual factors (season, occasion)
   * @param {Object} preferenceOptions - Preference-based pairing options
   * @returns {Array} Ranked wine recommendations with scores and explanations
   */
  findWinesForMeal(
    meal,
    wineInventory,
    userProfile = {},
    context = {},
    preferenceOptions = {}
  ) {
    const meals = Array.isArray(meal) ? meal : [meal];

    const rankedWines = wineInventory
      .map((wine) => {
        const wineProfile = this.extractWineProfile(wine);

        // Calculate food pairing scores using enhanced algorithm
        const scores = meals.map((m) => {
          const foodProfile = this.extractFoodProfile(m);
          return this.getAdvancedPairingScore(
            foodProfile,
            wineProfile,
            userProfile,
            context
          ).score;
        });

        const averageFoodScore =
          scores.reduce((a, b) => a + b, 0) / scores.length;
        let finalScore = averageFoodScore;
        let similarityScore = 0;

        // NEW V4: Preference-based pairing
        if (preferenceOptions.enabled && preferenceOptions.ghostWineProfile) {
          similarityScore =
            this.calculateSimilarity(
              wineProfile,
              preferenceOptions.ghostWineProfile
            ) * 100;

          // Combine scores based on user's bias
          const normalizedFoodScore = ((averageFoodScore + 25) / 50) * 100;
          const bias = preferenceOptions.pairingBias || 0.5;

          finalScore =
            normalizedFoodScore * (1 - bias) + similarityScore * bias;
        }

        const analysis = this.getAdvancedPairingScore(
          this.extractFoodProfile(meals[0]),
          wineProfile,
          userProfile,
          context
        );

        const confidence = this._getConfidenceLevel(finalScore);

        return {
          wine,
          pairingScore: averageFoodScore,
          finalScore,
          similarityScore,
          normalizedScore: Math.max(0, Math.min(100, (finalScore + 20) * 2.5)),
          explanation: analysis.interpretation,
          confidence,
          pairingType: finalScore > 5 ? "complementary" : "contrasting",
          rationale: analysis.rationale,
          colorClass: analysis.colorClass,
          breakdown: meals.map((m) => ({
            foodName: m.name,
            ...this.getAdvancedPairingScore(
              this.extractFoodProfile(m),
              wineProfile,
              userProfile,
              context
            ),
          })),
        };
      })
      .sort((a, b) => b.finalScore - a.finalScore);

    return rankedWines;
  }

  /**
   * Find optimal meal pairings for a given wine (Enhanced V4)
   * @param {Object} wine - Wine details
   * @param {Array} menuItems - Available dishes to pair with
   * @param {Object} userProfile - User preferences
   * @param {Object} context - Contextual factors
   * @returns {Array} Ranked meal recommendations
   */
  findMealsForWine(wine, menuItems, userProfile = {}, context = {}) {
    const wineProfile = this.extractWineProfile(wine);

    const rankedMeals = menuItems
      .map((meal) => {
        const foodProfile = this.extractFoodProfile(meal);
        const analysis = this.getAdvancedPairingScore(
          foodProfile,
          wineProfile,
          userProfile,
          context
        );

        const confidence = this._getConfidenceLevel(analysis.score);

        return {
          meal,
          pairingScore: analysis.score,
          normalizedScore: Math.max(
            0,
            Math.min(100, (analysis.score + 20) * 2.5)
          ),
          explanation: analysis.interpretation,
          confidence,
          pairingType: analysis.score > 5 ? "complementary" : "contrasting",
          rationale: analysis.rationale,
          colorClass: analysis.colorClass,
        };
      })
      .sort((a, b) => b.pairingScore - a.pairingScore);

    return rankedMeals;
  }

  /**
   * Enhanced pairing analysis with personalization (V4)
   * @param {Object} wine - Wine object
   * @param {Object} meal - Meal object
   * @param {Object} userProfile - User preferences
   * @param {Object} context - Contextual factors
   * @returns {Object} Detailed pairing analysis
   */
  analyzePairing(wine, meal, userProfile = {}, context = {}) {
    const wineProfile = this.extractWineProfile(wine);
    const foodProfile = this.extractFoodProfile(meal);
    const analysis = this.getAdvancedPairingScore(
      foodProfile,
      wineProfile,
      userProfile,
      context
    );

    // Extract key factors from rationale
    const factors = {};
    analysis.rationale.forEach((item) => {
      if (item.reason.includes("acid")) factors.acidity = item.reason;
      if (item.reason.includes("tannin")) factors.tannins = item.reason;
      if (item.reason.includes("intensity") || item.reason.includes("body"))
        factors.body = item.reason;
      if (item.reason.includes("sweet")) factors.sweetness = item.reason;
      if (item.reason.includes("spice")) factors.spice = item.reason;
      if (
        item.reason.includes("temperature") ||
        item.reason.includes("Temperature")
      )
        factors.temperature = item.reason;
    });

    return {
      score: analysis.score,
      normalizedScore: Math.max(0, Math.min(100, (analysis.score + 20) * 2.5)),
      explanation: analysis.interpretation,
      factors,
      rationale: analysis.rationale,
      colorClass: analysis.colorClass,
      personalized:
        userProfile.tanninSensitivity !== 1.0 ||
        userProfile.acidPreference !== 1.0,
      recommendations: {
        servingTemp: this.getServingTempRecommendation(wine),
        glassware: this.getGlasswareRecommendation(wine),
        timing: this.getTimingRecommendation(analysis.score),
        optimization: this.getOptimizationTips(wine, meal, analysis),
      },
    };
  }

  /**
   * Get optimization tips for improving the pairing
   */
  getOptimizationTips(wine, meal, analysis) {
    const tips = [];

    // Temperature optimization
    const wineProfile = this.extractWineProfile(wine);
    if (wineProfile.currentTemp && wineProfile.idealTemp) {
      const tempDiff = Math.abs(
        wineProfile.currentTemp - wineProfile.idealTemp
      );
      if (tempDiff > 2) {
        const direction =
          wineProfile.currentTemp > wineProfile.idealTemp ? "chill" : "warm";
        tips.push(
          `${
            direction.charAt(0).toUpperCase() + direction.slice(1)
          } the wine to ${wineProfile.idealTemp}°C for optimal pairing`
        );
      }
    }

    // Decanting recommendations for high-tannin wines
    if (wineProfile.tannin > 7 && wineProfile.tanninQuality < 0.8) {
      tips.push(
        "Consider decanting this wine 30-60 minutes before serving to soften tannins"
      );
    }

    // Food preparation tips
    const foodProfile = this.extractFoodProfile(meal);
    if (analysis.score < 0 && foodProfile.spice > 6) {
      tips.push(
        "Consider serving with dairy or bread to moderate spice levels"
      );
    }

    if (tips.length === 0) {
      tips.push("This pairing is well-optimized as is");
    }

    return tips;
  }

  /**
   * Generate serving temperature recommendation
   */
  getServingTempRecommendation(wine) {
    const wineType = (wine.type || wine.category || "").toLowerCase();
    if (wineType.includes("red")) return "16-18°C (60-65°F)";
    if (wineType.includes("white")) return "8-12°C (46-54°F)";
    if (wineType.includes("sparkling")) return "6-8°C (43-46°F)";
    if (wineType.includes("dessert")) return "8-10°C (46-50°F)";
    return "12-16°C (54-60°F)";
  }

  /**
   * Generate glassware recommendation
   */
  getGlasswareRecommendation(wine) {
    const wineName = (wine.name || "").toLowerCase();
    if (wineName.includes("cabernet") || wineName.includes("bordeaux"))
      return "Bordeaux glass";
    if (wineName.includes("pinot") || wineName.includes("burgundy"))
      return "Burgundy glass";
    if (wineName.includes("chardonnay")) return "Chardonnay glass";
    if (wineName.includes("champagne") || wineName.includes("sparkling"))
      return "Flute or coupe";

    const wineType = (wine.type || wine.category || "").toLowerCase();
    if (wineType.includes("red")) return "Red wine glass";
    if (wineType.includes("white")) return "White wine glass";
    return "Universal wine glass";
  }

  /**
   * Generate timing recommendation
   */
  getTimingRecommendation(score) {
    if (score > 15) return "Serve immediately - this pairing is exceptional";
    if (score > 5) return "Serve with confidence - this is a solid pairing";
    if (score > -5)
      return "Consider alternative options - this pairing has risks";
    return "Avoid this pairing - likely to clash";
  }

  /**
   * Bulk analyze all possible pairings for a restaurant
   * @param {Array} wines - Restaurant's wine list
   * @param {Array} dishes - Restaurant's menu items
   * @returns {Object} Complete pairing matrix with recommendations
   */
  generatePairingMatrix(wines, dishes) {
    const matrix = {};
    const recommendations = {
      topPairings: [],
      avoid: [],
      byCategory: {},
    };

    for (const wine of wines) {
      matrix[wine.id] = {};
      const wineResults = [];

      for (const dish of dishes) {
        const analysis = this.analyzePairing(wine, dish);
        matrix[wine.id][dish.id] = analysis;

        wineResults.push({
          wine,
          dish,
          score: analysis.score,
          analysis,
        });

        // Track exceptional pairings
        if (analysis.score > 15) {
          recommendations.topPairings.push({
            wine: wine.name,
            dish: dish.name,
            score: analysis.score,
            explanation: analysis.explanation,
          });
        }

        // Track problematic pairings
        if (analysis.score < -5) {
          recommendations.avoid.push({
            wine: wine.name,
            dish: dish.name,
            score: analysis.score,
            explanation: analysis.explanation,
          });
        }
      }

      // Find best pairing for this wine
      const sortedResults = wineResults.toSorted((a, b) => b.score - a.score);
      const bestPairing = sortedResults[0];
      if (bestPairing) {
        if (!recommendations.byCategory[wine.category]) {
          recommendations.byCategory[wine.category] = [];
        }
        recommendations.byCategory[wine.category].push({
          wine: wine.name,
          bestDish: bestPairing.dish.name,
          score: bestPairing.score,
        });
      }
    }

    // Sort recommendations
    recommendations.topPairings.sort((a, b) => b.score - a.score);
    recommendations.avoid.sort((a, b) => a.score - b.score);

    return {
      matrix,
      recommendations,
      summary: {
        totalPairings: wines.length * dishes.length,
        excellentPairings: recommendations.topPairings.length,
        problematicPairings: recommendations.avoid.length,
        averageCompatibility: this.calculateAverageCompatibility(matrix),
      },
    };
  }

  /**
   * Calculate average compatibility across all pairings
   */
  calculateAverageCompatibility(matrix) {
    let totalScore = 0;
    let count = 0;

    Object.values(matrix).forEach((wineMatrix) => {
      Object.values(wineMatrix).forEach((analysis) => {
        totalScore += analysis.score;
        count++;
      });
    });

    return count > 0 ? parseFloat((totalScore / count).toFixed(2)) : 0;
  }

  /**
   * Get pairing recommendations for a specific wine category
   * @param {string} category - Wine category (red, white, sparkling, etc.)
   * @param {Array} menuItems - Available menu items
   * @param {Array} wines - Wines in the category
   * @returns {Object} Category-specific pairing recommendations
   */
  getCategoryPairings(category, menuItems, wines) {
    const categoryWines = wines.filter((wine) =>
      (wine.category || wine.type || "")
        .toLowerCase()
        .includes(category.toLowerCase())
    );

    if (categoryWines.length === 0) {
      return { category, wines: [], recommendations: [] };
    }

    const recommendations = [];

    for (const wine of categoryWines) {
      const pairings = this.findMealsForWine(wine, menuItems);
      const topPairings = pairings.slice(0, 3); // Top 3 for each wine

      recommendations.push({
        wine: wine.name,
        topPairings: topPairings.map((p) => ({
          dish: p.meal.name,
          score: p.pairingScore,
          explanation: p.explanation,
        })),
      });
    }

    return {
      category,
      wines: categoryWines.length,
      recommendations,
    };
  }

  /**
   * Apply base component matching rules (v4 core logic)
   * @private
   */
  _applyBaseMatching(foodProfile, wineProfile, rationale) {
    let score = 0;

    // Core pairing rules from v4
    if (foodProfile.fat > 5 && wineProfile.acidity > 6) {
      score += 7;
      rationale.push({
        points: "+7",
        reason: "Palate Cleansing: Acidity cuts through richness.",
      });
    }

    if (foodProfile.spice > 4 && wineProfile.sweetness > 3) {
      score += 7;
      rationale.push({
        points: "+7",
        reason: "Spice Soothing: Sweetness cools spice heat.",
      });
    }

    if (Math.abs(foodProfile.intensity - wineProfile.intensity) <= 2) {
      score += 5;
      rationale.push({
        points: "+5",
        reason: "Intensity Match: Well-balanced power.",
      });
    }

    // VETO rules
    if (wineProfile.acidity < foodProfile.acidity) {
      score -= 15;
      rationale.push({
        points: "-15",
        reason: "VETO - Acid Flattens: Wine tastes flat.",
      });
    }

    if (wineProfile.sweetness < foodProfile.sweetness) {
      score -= 15;
      rationale.push({
        points: "-15",
        reason: "VETO - Sweet Dries: Wine tastes bitter.",
      });
    }

    if (foodProfile.isOilyFish && wineProfile.tannin > 4) {
      score -= 20;
      rationale.push({
        points: "-20",
        reason:
          "VETO - Fishy Metal: Tannins and fish oils create metallic taste.",
      });
    }

    return score;
  }

  /**
   * Apply enhanced tannin quality logic (v5)
   * @private
   */
  _applyTanninQualityLogic(foodProfile, wineProfile, rationale) {
    let score = 0;
    const qualityModifier = 1 - (1 - (wineProfile.tanninQuality || 0.8)) * 0.75;

    // Rich food + high tannins with quality adjustment
    if (foodProfile.fat > 7 && wineProfile.tannin > 7) {
      const adjustedScore = 10 * qualityModifier;
      score += adjustedScore;
      rationale.push({
        points: `+${adjustedScore.toFixed(0)}`,
        reason:
          "Synergistic Pairing: Rich food softens tannins. (Bonus adjusted by tannin quality)",
      });
    }

    // Umami clash with quality mitigation
    if (foodProfile.umami > 6 && wineProfile.tannin > 5) {
      const adjustedPenalty = -10 * (2 - qualityModifier);
      score += adjustedPenalty;
      rationale.push({
        points: adjustedPenalty.toFixed(0),
        reason: "Umami Clash: Penalty adjusted by tannin quality.",
      });
    }

    // Bitter collision with quality mitigation
    if (foodProfile.bitterness > 5 && wineProfile.tannin > 6) {
      const adjustedPenalty = -7 * (2 - qualityModifier);
      score += adjustedPenalty;
      rationale.push({
        points: adjustedPenalty.toFixed(0),
        reason: "Bitter Collision: Penalty adjusted by tannin quality.",
      });
    }

    return score;
  }

  /**
   * Apply dynamic temperature modeling (v5)
   * @private
   */
  _applyTemperatureModeling(foodProfile, wineProfile, rationale) {
    let score = 0;

    // Basic temperature analysis (from v4)
    if (wineProfile.idealTemp && wineProfile.currentTemp) {
      const tempDiff = Math.abs(
        wineProfile.currentTemp - wineProfile.idealTemp
      );
      if (tempDiff <= 1) {
        score += 3;
        rationale.push({
          points: "+3",
          reason: "Temp Bonus: Wine is at its ideal temperature.",
        });
      } else if (tempDiff > 3) {
        const tempPenalty = -tempDiff * 1.5;
        score += tempPenalty;
        rationale.push({
          points: tempPenalty.toFixed(0),
          reason: `Temp Penalty: Wine is ${tempDiff}°C from ideal, altering its profile.`,
        });
      }
    }

    // NEW: Food-wine temperature contrast analysis
    if (foodProfile.temp === "hot" && wineProfile.currentTemp < 10) {
      score -= 2;
      rationale.push({
        points: "-2",
        reason: "Temp Contrast: Very cold wine with hot food can be jarring.",
      });
    }

    if (foodProfile.temp === "chilled" && wineProfile.currentTemp > 16) {
      score -= 2;
      rationale.push({
        points: "-2",
        reason: "Temp Contrast: Warm wine with a chilled dish is unbalanced.",
      });
    }

    return score;
  }

  /**
   * Apply aromatic bridge logic (NEW in v5)
   * @private
   */
  _applyAromaticBridgeLogic(foodProfile, wineProfile, rationale) {
    if (!foodProfile.aromaCompounds || !wineProfile.aromaCompounds) {
      return 0;
    }

    const sharedAromas = foodProfile.aromaCompounds.filter((compound) =>
      wineProfile.aromaCompounds.includes(compound)
    );

    if (sharedAromas.length > 0) {
      const aromaBonus = sharedAromas.length * 4;
      rationale.push({
        points: `+${aromaBonus}`,
        reason: `Aromatic Bridge: Shared compounds (${sharedAromas.join(
          ", "
        )}) create harmony.`,
      });
      return aromaBonus;
    }

    return 0;
  }

  /**
   * Apply problem ingredient solver (NEW in v5)
   * @private
   */
  _applyProblemIngredientSolver(foodProfile, wineProfile, rationale) {
    if (
      !foodProfile.problemIngredients ||
      foodProfile.problemIngredients.length === 0
    ) {
      return 0;
    }

    let score = 0;

    // Cynarin (artichoke) handling
    if (foodProfile.problemIngredients.includes("cynarin")) {
      let cynarinPenalty = -25;
      let mitigationFound = false;

      // Check for fat/frying mitigation
      if (foodProfile.cookingMethod === "frying" || foodProfile.fat > 6) {
        cynarinPenalty += 15;
        mitigationFound = true;
        rationale.push({
          points: "+15",
          reason:
            "Cynarin Mitigated: Fat from frying reduces the negative sweetening effect.",
        });
      }

      // Check for high-acid, bone-dry wine defense
      if (wineProfile.acidity > 8 && wineProfile.sweetness <= 1) {
        cynarinPenalty += 10;
        mitigationFound = true;
        rationale.push({
          points: "+10",
          reason:
            "Cynarin Defense: Bone-dry, high-acid wine resists the sweetening effect.",
        });
      }

      score += cynarinPenalty;

      if (!mitigationFound) {
        rationale.push({
          points: "-25",
          reason:
            "Problem Ingredient: Artichoke (cynarin) will make most wines taste unpleasantly sweet.",
        });
      }
    }

    return score;
  }
}

// Integration with existing AI service
export const integrateWithAIService = (aiService, pairingService) => {
  // Add pairing capabilities to existing AI service
  aiService.pairingService = pairingService;

  // Enhance existing wine enhancement with pairing data
  const originalEnhanceWine = aiService.enhanceWineWithAI;
  aiService.enhanceWineWithAI = async function (wine, restaurantMenu) {
    const enhanced = await originalEnhanceWine.call(this, wine);

    // Add pairing recommendations using the science-based algorithm
    if (restaurantMenu && restaurantMenu.length > 0) {
      const pairings = await pairingService.findMealsForWine(
        wine,
        restaurantMenu
      );
      const topThreePairings = pairings.slice(0, 3);

      enhanced.aiEnhanced.pairings = {
        ...enhanced.aiEnhanced.pairings,
        algorithmicPairings: topThreePairings,
        scienceBasedAnalysis: true,
        pairingAlgorithm: "Science-Based Wine-Meal Compatibility Engine",
        topRecommendation: topThreePairings[0] || null,
        pairingMatrix: await pairingService.generatePairingMatrix(
          [wine],
          restaurantMenu
        ),
      };

      // Add pairing summary to description
      if (topThreePairings.length > 0) {
        const bestPairing = topThreePairings[0];
        enhanced.aiEnhanced.pairingSummary = `Best pairing: ${
          bestPairing.meal.name
        } (Score: ${bestPairing.pairingScore.toFixed(1)}) - ${
          bestPairing.explanation
        }`;
      }
    }

    return enhanced;
  };

  // Add pairing analysis to food items
  aiService.enhanceFoodWithPairings = async function (menuItem, wineList) {
    if (!wineList || wineList.length === 0) return menuItem;

    const pairings = await pairingService.findWinesForMeal(menuItem, wineList);
    const topThreePairings = pairings.slice(0, 3);

    return {
      ...menuItem,
      aiEnhanced: {
        ...menuItem.aiEnhanced,
        winePairings: topThreePairings,
        pairingAlgorithm: "Science-Based Wine-Meal Compatibility Engine",
        topWineRecommendation: topThreePairings[0] || null,
      },
    };
  };
};

// Create a singleton instance
export const pairingService = new PairingService();

export default PairingService;
